import type { TourProps } from 'antd';
import { surfaceForPath as helpSurfaceForPath, type SurfaceId } from '@/help/surfaceRegistry';

/**
 * Tour-engine pure helpers (C1). Tours are DATA — this module holds ONLY the glue between a contract
 * `EduTourStep` and AntD `Tour`: the stable `data-tour` anchor ids we add to key elements, the
 * surface↔route map, a validated placement map, and a resilient target resolver. No tour CONTENT
 * lives here (that is Track A's seeded data).
 */

type TourStep = NonNullable<TourProps['steps']>[number];
type Placement = TourStep['placement'];

/**
 * Stable `data-tour` anchor ids added to key designer/workbench elements the seeded tour targets.
 * A seeded step's `target` may be either one of these bare tokens (resolved as `[data-tour="…"]`)
 * or any raw CSS selector. Keep these in sync with the elements that carry the attribute.
 */
export const DATA_TOUR = {
  // Designer (the flagship loop: build → configure → break → simulate → publish)
  palette: 'designer-palette',
  canvas: 'designer-canvas',
  save: 'designer-save',
  validate: 'designer-validate',
  diff: 'designer-diff',
  publish: 'designer-publish',
  inspector: 'designer-inspector',
  problems: 'designer-problems',
  // Workbench (the analyst first-run)
  workbenchQueues: 'workbench-queues',
  workbenchList: 'workbench-list',
  // Global
  help: 'global-help',
} as const;

/** The surface the first-run offer looks up its flagship tour under. Track A seeds it here. */
export const FIRST_RUN_SURFACE = 'first-run';

/**
 * Help surface → the tour `surface` TAG Track A scopes seeded tours by. Route parsing now lives in
 * ONE place (`@/help/surfaceRegistry`, OPSFL-HELP-1); the tags here are unchanged because seeded
 * tour DATA is keyed by them (e.g. both the flows list and the designer share the `designer` tours,
 * and surfaces without their own tours fall back to `workbench` — the pre-registry behaviour).
 */
const TOUR_SURFACE_TAG: Record<SurfaceId, string> = {
  workbench: 'workbench',
  runs: 'workbench',
  bulk: 'workbench',
  'bulk-batch': 'workbench',
  'case-detail': 'workbench',
  entities: 'entity',
  'entity-detail': 'entity',
  flows: 'designer',
  designer: 'designer',
  templates: 'templates',
  screens: 'workbench',
  simulations: 'workbench',
  'migration-review': 'migration',
  oversight: 'oversight',
  integrations: 'integrations',
  analytics: 'workbench',
  alerts: 'workbench',
  admin: 'admin',
};

/** Map an app route → the tour `surface` tag Track A scopes tours by (Help menu / in-place tours). */
export function surfaceForPath(pathname: string): string {
  return TOUR_SURFACE_TAG[helpSurfaceForPath(pathname)];
}

/** Best-effort landing route for a tour's surface (used only by the first-run navigate hop). */
export function homePathForSurface(surface: string): string | null {
  switch (surface) {
    case FIRST_RUN_SURFACE:
    case 'designer':
      return '/flows/new';
    case 'workbench':
      return '/workbench';
    case 'templates':
      return '/templates';
    case 'integrations':
      return '/integrations';
    case 'oversight':
      return '/oversight';
    case 'admin':
      return '/admin';
    case 'migration':
      return '/migration';
    default:
      return null;
  }
}

const PLACEMENTS = new Set<string>([
  'top',
  'topLeft',
  'topRight',
  'bottom',
  'bottomLeft',
  'bottomRight',
  'left',
  'leftTop',
  'leftBottom',
  'right',
  'rightTop',
  'rightBottom',
  'center',
]);

/** Pass a contract placement through only when it is a valid AntD Tour placement; else undefined. */
export function toPlacement(p?: string): Placement {
  return p && PLACEMENTS.has(p) ? (p as Placement) : undefined;
}

function escapeAttr(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/"/g, '\\"');
}

/**
 * Resolve a step `target` (a raw CSS selector OR a bare `data-tour` token) to an element getter for
 * AntD Tour. A token with no CSS metacharacters is matched as `[data-tour="<token>"]`. A missing
 * element yields null — AntD Tour then centers the step with no spotlight, so a step whose target is
 * on another surface (or not yet mounted) NEVER crashes and never blocks. Re-evaluated per render.
 */
export function targetGetter(target: string): () => HTMLElement | null {
  return () => {
    if (!target) return null;
    const looksLikeSelector = /[.#[\]>:\s]/.test(target);
    let el: Element | null = null;
    if (looksLikeSelector) {
      try {
        el = document.querySelector(target);
      } catch {
        el = null;
      }
    }
    if (!el) {
      try {
        el = document.querySelector(`[data-tour="${escapeAttr(target)}"]`);
      } catch {
        el = null;
      }
    }
    if (!el && !looksLikeSelector) {
      try {
        el = document.querySelector(target);
      } catch {
        el = null;
      }
    }
    return (el as HTMLElement | null) ?? null;
  };
}
